import React from "react";
import { FileSpreadsheet, MapPin, CheckCircle2, ExternalLink } from "lucide-react";

const REPORTS = [
  {
    key: "orders",
    tag: "Sales · Orders · Regional",
    name: "All Orders Report (by last update / order date)",
    powers: "Revenue, monthly sales, order details, regional/marketplace breakdown, product performance",
    where: "Seller Central → Reports → Fulfilment → Sales → 'All Orders'. Or Reports → Order Reports (Amazon FBA & FBM).",
    path: "Reports ▸ Fulfilment ▸ All Orders",
    columns: "amazon-order-id, purchase-date, sku, asin, product-name, quantity, item-price, currency, sales-channel, ship-country/state",
    accent: "#007AFF",
    primary: true,
  },
  {
    key: "business",
    tag: "Traffic · Conversion",
    name: "Business Reports — Detail Page Sales & Traffic by Child ASIN",
    powers: "Units ordered, ordered product sales, sessions, buy-box %, conversion by ASIN",
    where: "Seller Central → Reports → Business Reports → 'Detail Page Sales and Traffic'.",
    path: "Reports ▸ Business Reports",
    columns: "(Child) ASIN, Title, Units Ordered, Ordered Product Sales, Sessions, Buy Box %",
    accent: "#8B5CF6",
  },
  {
    key: "settlement",
    tag: "Costs · Fees · Profit",
    name: "Payments / Settlement Report (Date Range Transactions)",
    powers: "Exact referral fees, FBA fees, refunds, and net proceeds for true profit",
    where: "Seller Central → Reports → Payments → 'Date Range Reports' (Transaction) or 'All Statements'.",
    path: "Reports ▸ Payments ▸ Date Range",
    columns: "order-id, type, amount-type, amount-description, amount, fee breakdowns",
    accent: "#F59E0B",
  },
  {
    key: "inventory",
    tag: "Inventory · Current Stock",
    name: "FBA — Manage Inventory / Inventory Ledger",
    powers: "Current FBA stock, fulfillable/reserved/inbound quantities per SKU",
    where: "Seller Central → Reports → Fulfilment → Inventory → 'Manage FBA Inventory' or 'Inventory Ledger'.",
    path: "Reports ▸ Fulfilment ▸ Inventory",
    columns: "sku, asin, afn-fulfillable-quantity, afn-reserved-quantity, afn-inbound-shipped-quantity",
    accent: "#10B981",
  },
  {
    key: "shipments",
    tag: "FBA Inbound",
    name: "FBA Inbound Shipment Items / Shipping Queue",
    powers: "Units shipped to Amazon FBA, shipment status & destination fulfillment center",
    where: "Seller Central → Inventory → Manage FBA Shipments → download shipment contents, or Reports → Fulfilment → Inbound.",
    path: "Inventory ▸ Manage FBA Shipments",
    columns: "shipment-id, sku, quantity-shipped, shipment-status, destination",
    accent: "#EF4444",
  },
];

export default function ReportsGuideTab() {
  return (
    <div className="space-y-6 max-w-5xl">
      <div className="bg-gradient-to-br from-[#007AFF]/10 to-transparent border border-[#007AFF]/20 rounded-xl p-6">
        <h2 className="font-head text-2xl font-bold tracking-tight text-white">Which Amazon reports should you use?</h2>
        <p className="mt-2 text-sm text-[#A3A3A3] max-w-2xl">
          All reports below are free exports from <span className="text-white font-semibold">Amazon Seller Central</span>.
          Download them as CSV/TSV/Excel and upload via the <span className="text-[#007AFF] font-semibold">Upload Report</span> button —
          the dashboard auto-detects each type. Start with the <span className="text-white font-semibold">All Orders</span> report for instant sales analytics.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {REPORTS.map((r) => (
          <div
            key={r.key}
            data-testid={`guide-${r.key}`}
            className={`bg-[#0f0f0f] border rounded-xl p-5 transition-colors duration-200 hover:border-white/15 ${r.primary ? "border-[#007AFF]/40 lg:col-span-2" : "border-white/5"}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${r.accent}1a`, color: r.accent }}>
                  <FileSpreadsheet className="h-5 w-5" />
                </div>
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: r.accent }}>{r.tag}</span>
                  <h3 className="font-head font-bold text-white leading-tight">{r.name}</h3>
                </div>
              </div>
              {r.primary && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#007AFF] text-white px-2 py-1 rounded-full shrink-0">Start here</span>}
            </div>

            <div className="mt-4 space-y-2.5 text-sm">
              <p className="flex gap-2 text-[#d0d0d0]"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" style={{ color: r.accent }} /><span><span className="text-[#888]">Powers: </span>{r.powers}</span></p>
              <p className="flex gap-2 text-[#d0d0d0]"><MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[#666]" /><span><span className="text-[#888]">Where: </span>{r.where}</span></p>
              <div className="flex items-center gap-2 text-xs text-[#666] pt-1">
                <span className="px-2 py-1 rounded bg-[#141414] border border-[#262626] tnum">{r.path}</span>
              </div>
              <p className="text-xs text-[#555] pt-1"><span className="text-[#666]">Key columns: </span>{r.columns}</p>
            </div>
          </div>
        ))}
      </div>

      <a
        href="https://sellercentral.amazon.com/reportcentral"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="seller-central-link"
        className="inline-flex items-center gap-2 text-sm text-[#007AFF] hover:text-[#4da3ff] transition-colors duration-200 font-semibold"
      >
        Open Seller Central Report Central <ExternalLink className="h-4 w-4" />
      </a>
    </div>
  );
}
